import React, { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  CircularProgress,
  Typography,
  Divider,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ErrorOutline as UnmappedIcon,
  RemoveCircleOutline as NotApplicableIcon,
  HourglassEmpty as PendingIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useSnackbar } from 'notistack';

export interface VendorMapping {
  standardizedName: string | null;
  originalExtracted: string | null;
  mappingStatus: 'auto_matched' | 'suggested' | 'nurse_confirmed' | 'nurse_overridden' | 'unmapped' | 'not_applicable' | null;
  suggestionSource?: 'cross_ref' | 'ai' | null;
  confidence?: number | null;
  suggestedAlternatives?: Array<{ standardizedName: string; confidence: number }>;
  mappedAt?: string | null;
  mappedBy?: string | null;
}

interface SuggestionOption {
  standardizedName: string;
  category?: string;
}

interface MappingStatusChipProps {
  reportId: string;
  resultIndex: number;
  /** The exact rows (backend R16): the server then acts on these, not on the index */
  rowIds?: { resultRowId?: string; pageNumber?: number; pageRowId?: string };
  vendorMapping: VendorMapping | null | undefined;
  originalExtractedName?: string;
  unitFromRow?: string | null;
  vendorName?: string | null;
  onUpdated: (newMapping: VendorMapping, newSummary?: any) => void;
}

/**
 * Per-row mapping chip — strict two-state UI.
 *
 *   Green "Mapped"   → auto_matched / nurse_confirmed / nurse_overridden
 *   Red "Unmapped"   → anything else; opens Change picker (vendor-only deterministic fuzzy)
 *   Grey "N/A"       → not_applicable
 *
 * Confidence percentages and AI-derived suggestions are NOT shown — by design. The matcher
 * either commits to a clean deterministic auto-match or hands the row to the nurse for an
 * explicit pick. The Change picker also offers an "Add as new parameter" CTA that routes to
 * the existing admin approval queue.
 */
const MappingStatusChip: React.FC<MappingStatusChipProps> = ({
  reportId,
  resultIndex,
  rowIds,
  vendorMapping,
  originalExtractedName,
  unitFromRow,
  vendorName,
  onUpdated
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestionOption[]>([]);
  const [selected, setSelected] = useState<SuggestionOption | null>(null);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Add-as-new-parameter inline form state. The nurse only confirms the NAME (prefilled from what
  // Gemini extracted) — there is no "Parameter ID" to type; it is derived behind the scenes.
  const [mode, setMode] = useState<'pick' | 'addNew'>('pick');
  const [newParamName, setNewParamName] = useState('');
  const [submittingNew, setSubmittingNew] = useState(false);
  const [addNewError, setAddNewError] = useState<string | null>(null);

  // Hidden for legacy reports
  if (!vendorMapping || !vendorMapping.mappingStatus) return null;
  const status = vendorMapping.mappingStatus;
  const extractedForDialog = originalExtractedName || vendorMapping.originalExtracted || '';

  const fetchSuggestions = async () => {
    setLoadingSuggestions(true);
    try {
      const response = await api.get(`/review/${reportId}/mapping/suggestions/${resultIndex}`);
      setSuggestions(response.data.suggestions || []);
    } catch (err: any) {
      console.error('[MappingStatusChip] Failed to fetch suggestions:', err.message);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const openPicker = async () => {
    setSelected(null);
    setMode('pick');
    setAddNewError(null);
    setNewParamName('');
    setPickerOpen(true);
    await fetchSuggestions();
  };

  const sendUpdate = async (standardizedName: string) => {
    setBusy(true);
    try {
      const response = await api.put(`/review/${reportId}/mapping/${resultIndex}`, {
        standardizedName,
        action: 'override',
        ...(rowIds || {})
      });
      if (response.data.success) {
        // Pass the server's authoritative summary so the parent can update the header counts
        // locally WITHOUT a full report refetch (which would reset the nurse's scroll position).
        onUpdated(response.data.vendorMapping, response.data.vendorMappingSummary);
      }
    } catch (err: any) {
      console.error('[MappingStatusChip] Update failed:', err.message);
      // The server explains a refusal (409: another row with the same name and value, or the row is gone)
      const serverMessage = err?.response?.data?.message;
      if (typeof serverMessage === 'string' && serverMessage) enqueueSnackbar(serverMessage, { variant: 'error' });
    } finally {
      setBusy(false);
      setPickerOpen(false);
    }
  };

  const handlePickConfirm = async () => {
    if (!selected) return;
    await sendUpdate(selected.standardizedName);
  };

  const handleAddNewSubmit = async () => {
    const finalName = (newParamName || extractedForDialog || '').trim();
    if (!finalName) {
      setAddNewError('Parameter name is required');
      return;
    }
    setAddNewError(null);
    setSubmittingNew(true);
    try {
      // parameterId is derived from the name automatically (kept only for the legacy global path /
      // doc shape); for the vendor path the backend ignores it and keys on {vendorName, name}.
      const derivedId = finalName.toUpperCase().replace(/\s+/g, '_').replace(/[()]/g, '');
      // Infer a sensible value type from the row's value so the nurse needn't pick one.
      const valueType = /^[\d.,\-+/<>%\s]+$/.test(String(unitFromRow ?? '')) || /\d/.test(finalName) ? 'numeric' : 'text';
      await api.post('/parameter-suggestions', {
        action: 'create',
        suggestedParameter: extractedForDialog || finalName,
        suggestedUnit: unitFromRow || null,
        reportId,
        vendorName: vendorName || null,
        newParameterData: {
          parameterId: derivedId,
          parameterName: finalName,
          valueType,
          possibleUnits: unitFromRow ? [unitFromRow] : [],
          description: ''
        }
      });
      // Local-only signal that the row is awaiting admin approval — no schema change.
      onUpdated({
        ...vendorMapping,
        // Existing enum value; the parent UI will show a "pending" hint based on
        // the presence of the suggestion ID in /parameter-suggestions.
        mappingStatus: vendorMapping.mappingStatus,
        standardizedName: vendorMapping.standardizedName
      });
      setPickerOpen(false);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Submission failed';
      setAddNewError(msg);
    } finally {
      setSubmittingNew(false);
    }
  };

  // ===== Rendering — status chip + ALWAYS-ACTIVE Change button =====
  // Every row, whatever its status, shows a state chip plus a Change button so the nurse can
  // re-map at any time (correct a wrong auto-match, re-pick, or add a new parameter).

  const isMapped = status === 'auto_matched' || status === 'nurse_confirmed' || status === 'nurse_overridden';

  let statusChip;
  if (isMapped) {
    statusChip = (
      <Tooltip title={vendorMapping.standardizedName ? `Mapped to ${vendorMapping.standardizedName}` : 'Mapped'}>
        <Chip
          icon={<CheckIcon fontSize="small" />}
          label={vendorMapping.standardizedName ? `→ ${vendorMapping.standardizedName}` : 'Mapped'}
          size="small"
          sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 500, maxWidth: 240 }}
        />
      </Tooltip>
    );
  } else if (status === 'not_applicable') {
    statusChip = (
      <Tooltip title="No vendor master matched — pick a parameter or add a new one">
        <Chip
          icon={<NotApplicableIcon fontSize="small" />}
          label="N/A"
          size="small"
          sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 500 }}
        />
      </Tooltip>
    );
  } else {
    statusChip = (
      <Tooltip title="Could not map automatically — click Change to pick or add a new parameter">
        <Chip
          icon={<UnmappedIcon fontSize="small" />}
          label="Unmapped"
          size="small"
          sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 500 }}
        />
      </Tooltip>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {statusChip}
        <Button
          size="small"
          variant={isMapped ? 'text' : 'outlined'}
          disabled={busy}
          onClick={openPicker}
          sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
        >
          Change
        </Button>
      </Box>

      {/* disableRestoreFocus: on close MUI otherwise restores focus to the (now re-rendered)
          trigger and scrolls it into view, yanking the nurse to the top of a long report. */}
      <Dialog open={pickerOpen} onClose={() => !busy && !submittingNew && setPickerOpen(false)} maxWidth="sm" fullWidth disableRestoreFocus>
        <DialogTitle>{mode === 'pick' ? 'Pick a standardized name' : 'Add as new parameter'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Original extracted: <strong>{extractedForDialog || '(unknown)'}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: isMapped ? 'success.main' : 'text.secondary' }}>
            Currently: <strong>{isMapped && vendorMapping.standardizedName ? vendorMapping.standardizedName : 'Unmapped'}</strong>
          </Typography>

          {mode === 'pick' && (
            <>
              {loadingSuggestions ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Autocomplete
                  options={[...suggestions, { standardizedName: '__ADD_NEW__', category: undefined }]}
                  getOptionLabel={(o) => o.standardizedName === '__ADD_NEW__' ? '+ Add as new parameter' : o.standardizedName}
                  value={selected}
                  onChange={(_, newValue) => {
                    if (newValue && newValue.standardizedName === '__ADD_NEW__') {
                      setNewParamName(extractedForDialog);  // prefill the extracted name
                      setMode('addNew');
                      setSelected(null);
                      setAddNewError(null);
                    } else {
                      setSelected(newValue);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Search vendor parameters" fullWidth autoFocus placeholder="Type to filter…" />
                  )}
                  renderOption={(props, option) => {
                    if (option.standardizedName === '__ADD_NEW__') {
                      return (
                        <li {...props} key="__ADD_NEW__" style={{ borderTop: '1px solid #E5E7EB', fontWeight: 500, color: '#4361EE', backgroundColor: '#F9FAFB' }}>
                          + Add &ldquo;{extractedForDialog}&rdquo; as a new parameter
                        </li>
                      );
                    }
                    return (
                      <li {...props} key={option.standardizedName}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{option.standardizedName}</span>
                          {option.category && (
                            <span style={{ opacity: 0.6, marginLeft: 8, fontSize: '0.85em' }}>{option.category}</span>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  noOptionsText="No vendor-master matches"
                  fullWidth
                />
              )}
            </>
          )}

          {mode === 'addNew' && (
            <>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                Add this parameter to <strong>{vendorName || 'this lab'}</strong>. It goes to the admin queue;
                once approved it appears here and auto-maps on future {vendorName || 'lab'} reports.
              </Typography>
              <TextField
                label="Parameter name"
                value={newParamName}
                onChange={(e) => setNewParamName(e.target.value)}
                fullWidth
                autoFocus
                sx={{ mb: 2 }}
                helperText="Pre-filled from the report — edit only if needed"
              />
              {unitFromRow && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
                  Unit (from report): <strong>{unitFromRow}</strong>
                </Typography>
              )}
              {addNewError && (
                <Typography variant="body2" sx={{ color: 'error.main', mb: 1 }}>
                  {addNewError}
                </Typography>
              )}
              <Button
                size="small"
                variant="text"
                onClick={() => { setMode('pick'); setAddNewError(null); }}
                disabled={submittingNew}
              >
                ← Back to vendor list
              </Button>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setPickerOpen(false)} disabled={busy || submittingNew}>Cancel</Button>
          {mode === 'pick' && (
            <Button
              variant="contained"
              disabled={!selected || busy}
              onClick={handlePickConfirm}
            >
              {busy ? <CircularProgress size={16} color="inherit" /> : 'Save'}
            </Button>
          )}
          {mode === 'addNew' && (
            <Button
              variant="contained"
              disabled={!newParamName.trim() || submittingNew}
              onClick={handleAddNewSubmit}
            >
              {submittingNew ? <CircularProgress size={16} color="inherit" /> : 'Submit for approval'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MappingStatusChip;
